import PropTypes from 'prop-types';
import Button from 'components/UI/Button';
import Select from 'components/UI/Select';
import { PAYMENT_FREQUENCIES } from 'constants/digitalLoan';
import {
  PAYMENT_TERM_RANGES,
  REQUESTED_AMOUNT_CONFIG,
} from 'constants/digitalLoanQuestionnaire';
import { formatDigitalLoanAmount } from '../utils/moneyInput';
import {
  getDefaultPaymentTerm,
  isLoanStepValid,
  normalizePaymentTerm,
} from '../utils/questionnaireValidation';
import RangeSlider from './RangeSlider';

const ApplicabilityLoanScreen = ({
  literals,
  questionnaire,
  onChange,
  onNext,
  onBack,
  disabled = false,
}) => {
  const frequencyOptions = [
    { value: PAYMENT_FREQUENCIES.BIWEEKLY, label: literals.frequencyOptions.biweekly },
    { value: PAYMENT_FREQUENCIES.MONTHLY, label: literals.frequencyOptions.monthly },
  ];

  const termRange = questionnaire.paymentFrequency
    ? PAYMENT_TERM_RANGES[questionnaire.paymentFrequency]
    : null;

  const termUnitLabel = termRange
    ? literals.termUnits[termRange.unitKey]
    : '';

  const canContinue = disabled || isLoanStepValid(questionnaire);

  const handleFrequencyChange = (paymentFrequency) => {
    onChange({
      paymentFrequency,
      paymentTerm: getDefaultPaymentTerm(paymentFrequency),
    });
  };

  const currentTerm = normalizePaymentTerm(
    questionnaire.paymentFrequency,
    questionnaire.paymentTerm,
  );

  return (
    <div className='full-screen-container digital-loan-questionnaire'>
      <div className='screen-content'>
        <h1 className='digital-loan-questionnaire__title text-primary font-bold text-center mb-2'>
          {literals.title}
        </h1>
        <p className='digital-loan-questionnaire__subtitle text-center mb-2'>{literals.subtitle}</p>
        <p className='digital-loan-questionnaire__required-note mb-4'>{literals.requiredNote}</p>

        <p className='font-bold mb-2'>{literals.amountQuestion}</p>
        <RangeSlider
          value={questionnaire.requestedAmount}
          min={REQUESTED_AMOUNT_CONFIG.min}
          max={REQUESTED_AMOUNT_CONFIG.max}
          step={REQUESTED_AMOUNT_CONFIG.step}
          minLabel={literals.amountMin}
          maxLabel={literals.amountMax}
          formattedValue={formatDigitalLoanAmount(questionnaire.requestedAmount)}
          onChange={(requestedAmount) => onChange({ requestedAmount })}
          disabled={disabled}
        />

        <p className='font-bold mb-2 mt-6'>{literals.frequencyQuestion}</p>
        <Select
          className='mb-4'
          label={literals.frequencyPlaceholder}
          options={frequencyOptions}
          value={questionnaire.paymentFrequency}
          onChange={handleFrequencyChange}
          disabled={disabled}
          canBeEmpty
          required
        />

        {termRange && (
          <>
            <p className='font-bold mb-2 mt-2'>{literals.termQuestion}</p>
            <RangeSlider
              value={currentTerm}
              values={termRange.options}
              minLabel={literals.termMin.replace('{count}', termRange.options[0])}
              maxLabel={literals.termMax
                .replace('{count}', termRange.options[termRange.options.length - 1])}
              formattedValue={literals.termValue
                .replace('{count}', currentTerm)
                .replace('{unit}', termUnitLabel)}
              onChange={(paymentTerm) => onChange({ paymentTerm })}
              disabled={disabled}
            />
          </>
        )}
      </div>
      <div className='screen-actions'>
        <Button text={literals.next} disabled={!canContinue} onClick={onNext} />
        <Button text={literals.back} type='secondary' onClick={onBack} />
      </div>
    </div>
  );
};

ApplicabilityLoanScreen.propTypes = {
  literals: PropTypes.object.isRequired,
  questionnaire: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

export default ApplicabilityLoanScreen;
